import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Check, X as XIcon, ChevronRight, Menu, X as MenuX, Star,
  Sparkles, Zap, LayoutGrid, Users, Wallet, TrendingUp,
  ClipboardCheck, Building2, PenSquare,
} from "lucide-react";

/* =============================================================================
   PLUTO — Public homepage at "/"
   Palette: warm beige/cream + deep purple-blue (indigo-700 → 900).
   Vibe:   Ahrefs-style bold hierarchy + notebook / doodled accents
           (Caveat handwriting, SVG squiggles, wobbly borders, washi-tape,
           product mocks pinned like polaroids).
   Copy:   Business-first — no technical jargon.
   Data:   All names & brands in the mocks are fictional / US.
============================================================================= */

const CREAM        = "#FBF6E9";
const CREAM_ALT    = "#F1E9D2";
const INK          = "#1E1B4B";
const INK_SOFT     = "#4C4A73";
const INDIGO       = "#4338CA";
const INDIGO_DEEP  = "#312E81";

export default function Home() {
  const nav = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      className="min-h-screen overflow-x-clip"
      style={{ background: CREAM, color: INK }}
      data-testid="landing-page"
    >
      <PaperTexture />
      <LandingHeader scrolled={scrolled} mobileNav={mobileNav} setMobileNav={setMobileNav} onLogin={() => nav("/login")} onCta={() => nav("/login")} />
      <Hero onCta={() => nav("/login")} />
      <TrustStrip />
      <FeatureBento />
      <ProductTour />
      <MetricsBand />
      <ComparisonMatrix />
      <Testimonials />
      <FinalCta onCta={() => nav("/login")} />
      <LandingFooter />
    </div>
  );
}

function PaperTexture() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-0 opacity-[0.35] mix-blend-multiply"
      style={{
        backgroundImage: "radial-gradient(rgba(30,27,75,0.08) 1px, transparent 1px)",
        backgroundSize: "3px 3px",
      }}
    />
  );
}

/* -----------------------------------------------------------------------------
   Reusable doodles
----------------------------------------------------------------------------- */
function SquiggleUnderline({ className = "", color = INDIGO }) {
  return (
    <svg viewBox="0 0 300 24" className={className} preserveAspectRatio="none" aria-hidden>
      <path d="M4 15 C 40 4, 80 22, 120 12 S 200 4, 240 15 S 296 8, 296 12"
            stroke={color} strokeWidth="4" strokeLinecap="round" fill="none" />
    </svg>
  );
}
function CurvyArrow({ className = "", color = INDIGO, flip = false }) {
  return (
    <svg viewBox="0 0 120 80" className={className} aria-hidden style={flip ? { transform: "scaleX(-1)" } : undefined}>
      <path d="M8 12 C 40 6, 70 10, 90 32 C 100 44, 100 58, 92 68"
            stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M88 60 L 92 70 L 100 62"
            stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function Circle({ className = "", color = INDIGO }) {
  return (
    <svg viewBox="0 0 160 60" className={className} aria-hidden>
      <path d="M20 30 C 20 8, 140 6, 148 28 C 154 48, 42 58, 22 44 C 12 34, 16 22, 40 18"
            stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" />
    </svg>
  );
}
function StarDoodle({ className = "", color = INDIGO }) {
  return (
    <svg viewBox="0 0 40 40" className={className} aria-hidden>
      <path d="M20 4 L20 36 M4 20 L36 20 M8 8 L32 32 M32 8 L8 32"
            stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
function CheckDoodle({ className = "", color = "#059669" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <path d="M4 13 C 6 13, 9 16, 10 18 C 12 15, 15 8, 22 4"
            stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function CrossDoodle({ className = "", color = "#94A3B8" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <path d="M5 5 Q 12 13, 19 19 M19 5 Q 12 13, 5 19"
            stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" />
    </svg>
  );
}

/* -----------------------------------------------------------------------------
   1. Header
----------------------------------------------------------------------------- */
function LandingHeader({ scrolled, mobileNav, setMobileNav, onLogin, onCta }) {
  const links = [
    { label: "Features", href: "#features" },
    { label: "How it works", href: "#tour" },
    { label: "Compare", href: "#compare" },
    { label: "Pricing", href: "#pricing" },
  ];
  return (
    <header
      data-testid="landing-header"
      className="fixed top-0 inset-x-0 z-50 transition-all duration-300"
      style={{
        background: scrolled ? "rgba(251,246,233,0.85)" : "transparent",
        backdropFilter: scrolled ? "blur(16px) saturate(1.2)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(16px) saturate(1.2)" : "none",
        borderBottom: scrolled ? "1px solid rgba(30,27,75,0.08)" : "1px solid transparent",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="landing-brand">
          <img src="/logo/pluto-logo.png" alt="PLUTO" className="h-9 w-9 object-contain group-hover:rotate-6 transition-transform" />
          <div className="flex flex-col leading-none">
            <span className="font-brand tracking-brand text-lg" style={{ color: INK }}>PLUTO</span>
            <span className="text-[9px] font-medium tracking-[0.18em] uppercase mt-0.5" style={{ color: INK_SOFT }}>by CreateX</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-[13px] font-medium opacity-70 hover:opacity-100 transition-opacity"
              style={{ color: INK }}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button onClick={onLogin} data-testid="header-login-btn"
                  className="hidden sm:inline-flex text-[13px] font-medium px-3 py-2 opacity-80 hover:opacity-100"
                  style={{ color: INK }}>
            Log in
          </button>
          <button onClick={onCta} data-testid="header-cta-btn"
                  className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white px-4 py-2 rounded-full transition-all hover:-translate-y-px shadow-[0_4px_0_0_#1E1B4B] hover:shadow-[0_6px_0_0_#1E1B4B]"
                  style={{ background: "linear-gradient(135deg, #4338CA 0%, #6D28D9 55%, #A21CAF 100%)" }}>
            Try it free <ArrowRight className="h-3.5 w-3.5" />
          </button>
          <button onClick={() => setMobileNav((v) => !v)}
                  className="md:hidden ml-1 p-2 rounded-lg" style={{ color: INK }}
                  data-testid="header-mobile-toggle">
            {mobileNav ? <MenuX className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {mobileNav && (
        <div className="md:hidden px-6 py-4 space-y-1 border-t"
             style={{ background: CREAM, borderColor: "rgba(30,27,75,0.08)" }}
             data-testid="mobile-nav-panel">
          {links.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setMobileNav(false)}
               className="block py-2 text-sm font-medium opacity-80 hover:opacity-100"
               style={{ color: INK }}>
              {l.label}
            </a>
          ))}
          <button onClick={onLogin} data-testid="mobile-login-btn"
                  className="block w-full text-left py-2 text-sm font-medium opacity-80"
                  style={{ color: INK }}>
            Log in
          </button>
        </div>
      )}
    </header>
  );
}

/* -----------------------------------------------------------------------------
   2. Hero
----------------------------------------------------------------------------- */
function Hero({ onCta }) {
  return (
    <section
      data-testid="hero-section"
      className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse 90% 60% at 50% 0%, #FFF9EA 0%, #FBF6E9 40%, #F6EFDD 100%), " +
          "radial-gradient(ellipse 60% 40% at 15% 40%, rgba(199,210,254,0.55) 0%, transparent 60%), " +
          "radial-gradient(ellipse 60% 40% at 85% 60%, rgba(221,214,254,0.45) 0%, transparent 60%)",
      }}
    >
      <div aria-hidden className="absolute -top-32 -left-24 h-96 w-96 rounded-full opacity-50 blur-[110px]"
           style={{ background: "linear-gradient(135deg, #C7D2FE 0%, #A5B4FC 100%)" }} />
      <div aria-hidden className="absolute top-1/2 -right-32 h-[28rem] w-[28rem] rounded-full opacity-40 blur-[120px]"
           style={{ background: "linear-gradient(135deg, #DDD6FE 0%, #C4B5FD 100%)" }} />
      <div aria-hidden className="absolute -bottom-20 left-1/3 h-72 w-72 rounded-full opacity-30 blur-[100px]"
           style={{ background: "linear-gradient(135deg, #FBCFE8 0%, #F5D0FE 100%)" }} />
      <div aria-hidden className="absolute inset-x-0 top-32 h-[500px] opacity-[0.06]"
           style={{ backgroundImage: "repeating-linear-gradient(to bottom, transparent 0, transparent 39px, #1E1B4B 39px, #1E1B4B 40px)" }} />

      <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 mb-6" data-testid="hero-eyebrow">
            <StarDoodle className="h-3.5 w-3.5" color={INDIGO} />
            <span className="text-xs font-semibold uppercase tracking-[0.24em]" style={{ color: INDIGO }}>
              Built for editorial teams
            </span>
            <StarDoodle className="h-3.5 w-3.5" color={INDIGO} />
          </div>

          <h1 className="font-display font-bold leading-[0.98] tracking-[-0.03em] text-[42px] sm:text-[64px] md:text-[76px] lg:text-[88px]"
              style={{ color: INK }} data-testid="hero-headline">
            The friendliest way<br />
            to run a{" "}
            <span className="relative inline-block whitespace-nowrap">
              <span
                style={{
                  backgroundImage: "linear-gradient(135deg, #4338CA 0%, #7C3AED 50%, #C026D3 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                content team.
              </span>
              <SquiggleUnderline className="absolute left-0 -bottom-3 md:-bottom-4 w-full h-4 md:h-5" color={INDIGO} />
            </span>
          </h1>

          <div className="mt-6" data-testid="hero-caveat">
            <span className="font-hand text-2xl md:text-3xl" style={{ color: INDIGO_DEEP }}>
              — planning, publishing &amp; paying, all in one place.
            </span>
          </div>

          <p className="mt-8 text-base md:text-lg max-w-xl mx-auto leading-relaxed"
             style={{ color: INK_SOFT }} data-testid="hero-subheadline">
            PLUTO gives bloggers and media companies a home for every story —
            from the first pitch to the moment it goes live, and everyone
            who touched it in between.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button onClick={onCta} data-testid="hero-primary-cta"
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold text-white transition-all hover:-translate-y-0.5 shadow-[0_5px_0_0_#1E1B4B] hover:shadow-[0_7px_0_0_#1E1B4B]"
                    style={{ background: "linear-gradient(135deg, #4338CA 0%, #6D28D9 55%, #A21CAF 100%)" }}>
              Start free trial <ArrowRight className="h-4 w-4" />
            </button>
            <Link to="/dash" data-testid="hero-secondary-cta"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold transition-all hover:-translate-y-0.5"
                  style={{ color: INK, border: `2px solid ${INK}` }}>
              See the dashboard <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          <p className="mt-5 text-xs" style={{ color: INK_SOFT }} data-testid="hero-microcopy">
            Free 14-day trial · No credit card · Bring your whole newsroom
          </p>
        </div>

        <div className="relative mt-16 md:mt-24" data-testid="hero-product-preview">
          <div className="hidden md:flex absolute -top-10 left-4 items-end gap-2 z-10 -rotate-[8deg]" data-testid="hero-doodle-note">
            <span className="font-hand text-2xl leading-tight" style={{ color: INDIGO_DEEP }}>
              your whole workflow<br />on one screen
            </span>
            <CurvyArrow className="h-16 w-20" color={INDIGO_DEEP} />
          </div>

          <PolaroidFrame rotate={-0.6} testid="hero-dashboard-mock">
            <DashboardMock />
          </PolaroidFrame>
        </div>
      </div>
    </section>
  );
}

/* -----------------------------------------------------------------------------
   Polaroid frame — pinned product mock, washi tape + soft shadow
----------------------------------------------------------------------------- */
function PolaroidFrame({ children, rotate = 0, testid, tape = true, browserLabel = "pluto.app" }) {
  return (
    <div className="relative mx-auto max-w-5xl" style={{ transform: `rotate(${rotate}deg)` }} data-testid={testid}>
      {tape && (
        <>
          <span aria-hidden
                className="absolute -top-3 left-10 h-6 w-20 z-20 -rotate-[8deg] rounded-sm opacity-90"
                style={{ background: "repeating-linear-gradient(45deg, #FCD34D 0 6px, #FBBF24 6px 12px)",
                         boxShadow: "0 2px 4px rgba(0,0,0,0.10)" }} />
          <span aria-hidden
                className="absolute -top-3 right-14 h-6 w-16 z-20 rotate-[6deg] rounded-sm opacity-90"
                style={{ background: "repeating-linear-gradient(-45deg, #C4B5FD 0 6px, #A78BFA 6px 12px)",
                         boxShadow: "0 2px 4px rgba(0,0,0,0.10)" }} />
        </>
      )}
      <div className="relative rounded-2xl overflow-hidden border-2"
           style={{ background: "white", borderColor: INK,
                    boxShadow: "8px 8px 0 0 rgba(30,27,75,0.14), 0 20px 60px -20px rgba(67,56,202,0.35)" }}>
        <div className="h-8 flex items-center gap-1.5 px-3 border-b"
             style={{ background: "#F5F0E1", borderColor: "rgba(30,27,75,0.10)" }}>
          <span className="h-2.5 w-2.5 rounded-full bg-rose-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
          <span className="ml-3 text-[10px] font-mono opacity-60" style={{ color: INK }}>{browserLabel}</span>
        </div>
        <div className="bg-white">{children}</div>
      </div>
    </div>
  );
}

/* ============================================================================
   PRODUCT MOCKS — CSS reconstructions of the real PLUTO UI with fictional
   US names and fictional brand names. All strings intentional; no real data.
============================================================================ */

// Fictional demo data
const TEAM_MEMBERS = [
  { initials: "SM", name: "Sarah Miller",     role: "Content Manager", color: "#4338CA" },
  { initials: "JA", name: "James Anderson",   role: "Team Lead",       color: "#7C3AED" },
  { initials: "ET", name: "Emma Thompson",    role: "Writer",          color: "#0891B2" },
  { initials: "MB", name: "Michael Brooks",   role: "Writer",          color: "#059669" },
  { initials: "OP", name: "Olivia Parker",    role: "Publisher",       color: "#D97706" },
];
const PROJECTS = ["moderncanvas.co", "northfield.blog", "ashwoodmedia.com", "silverlanepress.com", "lightspur.co"];
const KEYWORDS = [
  "how to plan a summer content calendar",
  "beginner's guide to editorial workflows",
  "10 newsroom tools worth your time",
  "the case for a slower content week",
  "small teams, big publishing habits",
  "why editors love a good spreadsheet",
  "what to publish on a monday morning",
];

/* Hero dashboard mock — pipeline + task list summary */
function DashboardMock() {
  return (
    <div className="flex" style={{ minHeight: 420 }}>
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-44 shrink-0 border-r py-3 px-2 gap-1"
             style={{ background: "#FBFAF6", borderColor: "rgba(30,27,75,0.06)" }}>
        {["Dashboard", "My tasks", "Projects", "People", "Payroll"].map((it, i) => (
          <div key={it}
               className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[11px] font-medium ${i === 0 ? "" : "opacity-60"}`}
               style={i === 0 ? { background: "#EEF0FE", color: INDIGO_DEEP } : { color: INK }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: i === 0 ? INDIGO : "#CBD5E1" }} />
            {it}
          </div>
        ))}
        <div className="mt-3 mb-1 px-2.5 text-[9px] font-bold uppercase tracking-widest opacity-40" style={{ color: INK }}>Departments</div>
        <div className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium" style={{ color: INK }}>
          <LayoutGrid className="h-3 w-3" style={{ color: INDIGO }} /> Content Team
        </div>
        <div className="ml-4 flex items-center gap-1 px-2 py-1 text-[11px]" style={{ color: INK_SOFT }}>
          <Users className="h-3 w-3" style={{ color: INDIGO }} /> Sarah Miller
        </div>
        <div className="ml-6 flex items-center gap-1 px-2 py-1 text-[11px]" style={{ color: INK_SOFT }}>
          <PenSquare className="h-2.5 w-2.5" style={{ color: "#7C3AED" }} /> James Anderson
        </div>
      </aside>
      {/* Main */}
      <div className="flex-1 p-5">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="font-display text-xl font-bold" style={{ color: INK }}>Content Dashboard</div>
            <div className="text-[11px] opacity-60" style={{ color: INK }}>Welcome back, Sarah.</div>
          </div>
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full text-white" style={{ background: INDIGO }}>
            <Sparkles className="h-3 w-3" /> This week
          </span>
        </div>
        {/* KPI cards */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[
            { l: "Stories",   v: "148" },
            { l: "This week", v: "12"  },
            { l: "In review", v: "6"   },
            { l: "Published", v: "34"  },
          ].map((k, i) => (
            <div key={i} className="rounded-lg border p-2.5" style={{ background: "#FDFCF7", borderColor: "rgba(30,27,75,0.10)" }}>
              <div className="text-[9px] font-bold uppercase tracking-widest opacity-60" style={{ color: INK }}>{k.l}</div>
              <div className="font-display text-lg font-bold mt-0.5" style={{ color: INK }}>{k.v}</div>
            </div>
          ))}
        </div>
        {/* Pipeline strip */}
        <div className="rounded-lg border p-3 mb-4" style={{ background: "#FDFCF7", borderColor: "rgba(30,27,75,0.10)" }}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] font-bold" style={{ color: INK }}>Pipeline</div>
            <div className="text-[9px] opacity-60" style={{ color: INK }}>148 stories</div>
          </div>
          <div className="flex gap-0.5 rounded-full h-2 overflow-hidden" style={{ background: "#F1E9D2" }}>
            <div className="h-full" style={{ width: "22%", background: "#CBD5E1" }} />
            <div className="h-full" style={{ width: "14%", background: "#FBBF24" }} />
            <div className="h-full" style={{ width: "10%", background: "#FB923C" }} />
            <div className="h-full" style={{ width: "22%", background: "#60A5FA" }} />
            <div className="h-full" style={{ width: "32%", background: "#34D399" }} />
          </div>
          <div className="grid grid-cols-5 gap-1 mt-2">
            {[
              { l: "To-Do",     v: 32, c: "#64748B" },
              { l: "Editing",   v: 21, c: "#F59E0B" },
              { l: "Publishing",v: 15, c: "#F97316" },
              { l: "Review",    v: 33, c: "#3B82F6" },
              { l: "Published", v: 47, c: "#10B981" },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-[8px] font-semibold uppercase tracking-widest opacity-70" style={{ color: s.c }}>{s.l}</div>
                <div className="font-display text-sm font-bold" style={{ color: INK }}>{s.v}</div>
              </div>
            ))}
          </div>
        </div>
        {/* Recent stories */}
        <div className="rounded-lg border" style={{ background: "#FDFCF7", borderColor: "rgba(30,27,75,0.10)" }}>
          <div className="px-3 py-2 border-b text-[11px] font-bold" style={{ color: INK, borderColor: "rgba(30,27,75,0.06)" }}>Recent stories</div>
          {KEYWORDS.slice(0, 4).map((k, i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-2 text-[11px] border-b last:border-b-0"
                 style={{ color: INK, borderColor: "rgba(30,27,75,0.04)" }}>
              <span className="font-medium flex-1 truncate">{k}</span>
              <span className="opacity-60 shrink-0">{PROJECTS[i % PROJECTS.length]}</span>
              <AvatarPill m={TEAM_MEMBERS[(i + 2) % TEAM_MEMBERS.length]} />
              <StatusChip s={["Editing", "Review", "Published", "To-Do"][i]} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
function AvatarPill({ m }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-medium">
      <span className="h-4 w-4 rounded-full flex items-center justify-center text-white text-[8px] font-bold"
            style={{ background: m.color }}>{m.initials}</span>
      <span className="opacity-70 hidden lg:inline">{m.name}</span>
    </span>
  );
}
function StatusChip({ s }) {
  const c = {
    "To-Do":     "bg-slate-100 text-slate-700 border-slate-200",
    "Editing":   "bg-amber-100 text-amber-700 border-amber-200",
    "Publishing":"bg-orange-100 text-orange-700 border-orange-200",
    "Review":    "bg-blue-100 text-blue-700 border-blue-200",
    "Published": "bg-emerald-100 text-emerald-700 border-emerald-200",
  }[s] || "";
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-semibold border ${c}`}>{s}</span>
  );
}

/* Spreadsheet mock — resembles the real ScopeView list view */
function SpreadsheetMock() {
  const rows = [
    { d: "07-22", kw: "how to plan a summer content calendar", p: "moderncanvas.co",    w: "Emma Thompson",  s: "Published"  },
    { d: "07-27", kw: "beginner's guide to editorial workflows", p: "northfield.blog", w: "Emma Thompson",  s: "Editing"    },
    { d: "08-03", kw: "10 newsroom tools worth your time",       p: "moderncanvas.co", w: "Michael Brooks", s: "To-Do"      },
    { d: "08-03", kw: "the case for a slower content week",      p: "moderncanvas.co", w: "Michael Brooks", s: "To-Do"      },
    { d: "08-03", kw: "small teams, big publishing habits",      p: "moderncanvas.co", w: "Emma Thompson",  s: "Publishing" },
    { d: "08-03", kw: "why editors love a good spreadsheet",     p: "ashwoodmedia.com", w: "Michael Brooks", s: "Review"     },
    { d: "08-04", kw: "what to publish on a monday morning",     p: "ashwoodmedia.com", w: "Emma Thompson",  s: "Published"  },
  ];
  return (
    <div className="p-3">
      {/* toolbar */}
      <div className="flex items-center gap-2 mb-2">
        <div className="flex items-center gap-1.5 h-7 w-40 px-2 rounded border text-[10px] opacity-60"
             style={{ borderColor: "rgba(30,27,75,0.15)", color: INK }}>
          Search stories…
        </div>
        <div className="flex items-center gap-1">
          {["All statuses", "All projects", "All writers"].map((f, i) => (
            <span key={i} className="h-7 inline-flex items-center px-2 rounded border text-[10px]"
                  style={{ borderColor: "rgba(30,27,75,0.15)", background: "white", color: INK }}>
              {f} ▾
            </span>
          ))}
        </div>
        <div className="ml-auto text-[10px] opacity-60" style={{ color: INK }}>148 stories</div>
      </div>
      <div className="rounded-lg border overflow-hidden" style={{ borderColor: "rgba(30,27,75,0.15)", background: "white" }}>
        <table className="w-full text-[10px]">
          <thead style={{ background: "#F1E9D2", borderBottom: `2px solid ${INK}` }}>
            <tr style={{ color: INK_SOFT }}>
              <th className="w-6 py-1.5"></th>
              <th className="w-8 py-1.5 text-center font-semibold uppercase tracking-widest">#</th>
              <th className="w-16 text-left px-2 py-1.5 font-semibold uppercase tracking-widest">Date</th>
              <th className="text-left px-2 py-1.5 font-semibold uppercase tracking-widest">Story</th>
              <th className="w-32 text-left px-2 py-1.5 font-semibold uppercase tracking-widest">Publication</th>
              <th className="w-28 text-left px-2 py-1.5 font-semibold uppercase tracking-widest">Writer</th>
              <th className="w-24 text-left px-2 py-1.5 font-semibold uppercase tracking-widest">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t"
                  style={{ borderColor: "rgba(30,27,75,0.06)",
                           background: i === 3 ? "rgba(199,210,254,0.20)" : "white" }}>
                <td className="text-center"><span className="h-2.5 w-2.5 rounded border inline-block"
                                                  style={{ borderColor: "rgba(30,27,75,0.30)" }} /></td>
                <td className="text-center opacity-50" style={{ color: INK }}>{i + 1}</td>
                <td className="px-2 py-1.5 font-mono opacity-70" style={{ color: INK }}>2026-{r.d}</td>
                <td className="px-2 py-1.5 font-medium" style={{ color: INK }}>{r.kw}</td>
                <td className="px-2 py-1.5" style={{ color: INDIGO }}><span className="hover:underline cursor-pointer">{r.p}</span></td>
                <td className="px-2 py-1.5" style={{ color: INK }}>{r.w}</td>
                <td className="px-2 py-1.5"><StatusChip s={r.s} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* Team hierarchy mock — Content Manager → Team Lead → Writers */
function HierarchyMock() {
  return (
    <div className="p-6" style={{ background: "#FDFCF7" }}>
      <div className="max-w-md mx-auto rounded-xl border p-5" style={{ background: "white", borderColor: "rgba(30,27,75,0.15)" }}>
        <div className="flex items-center gap-2 mb-3">
          <LayoutGrid className="h-4 w-4" style={{ color: INDIGO }} />
          <span className="text-sm font-semibold" style={{ color: INK }}>Content Team</span>
          <span className="ml-auto text-[10px] opacity-50" style={{ color: INK }}>3 desks</span>
        </div>
        <div className="pl-1">
          <NodeRow icon="cm" name="Sarah Miller"      role="Content Manager"    count={2} />
          <div className="ml-3.5 mt-1 space-y-1 border-l pl-3" style={{ borderColor: "rgba(30,27,75,0.10)" }}>
            <NodeRow icon="tl" name="James Anderson"  role="Editorial TL"       count={7} pill="Lifestyle" />
            <div className="ml-3.5 mt-1 space-y-0.5 border-l pl-3" style={{ borderColor: "rgba(30,27,75,0.06)" }}>
              {[
                { name: "Emma Thompson",  role: "Writer" },
                { name: "Michael Brooks", role: "Writer" },
                { name: "Olivia Parker",  role: "Publisher" },
              ].map((p) => <NodeRow key={p.name} icon="writer" name={p.name} role={p.role} small />)}
            </div>
            <NodeRow icon="tl" name="Grace Patterson" role="Reviews TL"          count={4} pill="Reviews" />
          </div>
        </div>
        <div className="mt-3 pl-1">
          <NodeRow icon="cm" name="Ethan Walker"       role="Content Manager"    count={1} />
        </div>
      </div>
    </div>
  );
}
function NodeRow({ icon, name, role, count, pill, small }) {
  const Icon = icon === "cm" ? Users : icon === "tl" ? PenSquare : Sparkles;
  const iconColor = icon === "cm" ? INDIGO : icon === "tl" ? "#7C3AED" : "#94A3B8";
  return (
    <div className={`flex items-center gap-2 py-1 px-1.5 rounded ${small ? "text-[11px]" : "text-[12px]"}`}>
      <Icon className="h-3 w-3 shrink-0" style={{ color: iconColor }} />
      <span className="font-medium truncate" style={{ color: INK }}>{name}</span>
      {pill && (
        <span className="text-[8px] font-bold uppercase tracking-wider px-1.5 py-px rounded-full"
              style={{ background: "#EEF0FE", color: INDIGO }}>
          {pill}
        </span>
      )}
      <span className="ml-auto opacity-50 shrink-0" style={{ color: INK, fontSize: 10 }}>{role}</span>
      {typeof count === "number" && <span className="font-semibold shrink-0 tabular-nums" style={{ color: INK, fontSize: 10 }}>{count}</span>}
    </div>
  );
}

/* Payroll mock — sample monthly sheet */
function PayrollMock() {
  const rows = [
    { emp: "Sarah Miller",     role: "Content Manager", days: 22, gross: "$5,200", ded: "$0",   credit: "$5,200" },
    { emp: "James Anderson",   role: "Editorial TL",    days: 22, gross: "$4,600", ded: "$0",   credit: "$4,600" },
    { emp: "Emma Thompson",    role: "Writer",          days: 21, gross: "$3,800", ded: "$172", credit: "$3,628" },
    { emp: "Michael Brooks",   role: "Writer",          days: 22, gross: "$3,800", ded: "$0",   credit: "$3,800" },
    { emp: "Olivia Parker",    role: "Publisher",       days: 22, gross: "$4,100", ded: "$0",   credit: "$4,100" },
    { emp: "Grace Patterson",  role: "Reviews TL",      days: 20, gross: "$4,500", ded: "$409", credit: "$4,091" },
  ];
  return (
    <div className="p-3">
      <div className="flex items-baseline gap-4 mb-3 px-1">
        <div>
          <div className="font-display text-base font-bold" style={{ color: INK }}>Payroll</div>
          <div className="text-[10px] opacity-60" style={{ color: INK }}>September 2026 · net payable</div>
        </div>
        <div className="ml-auto text-right">
          <div className="text-[9px] uppercase tracking-widest opacity-60" style={{ color: INK }}>To credit</div>
          <div className="font-display text-lg font-bold" style={{ color: INDIGO }}>$25,419</div>
        </div>
      </div>
      <div className="rounded-lg border overflow-hidden" style={{ borderColor: "rgba(30,27,75,0.15)", background: "white" }}>
        <table className="w-full text-[10px]">
          <thead style={{ background: "linear-gradient(90deg, #EEF0FE, #F3ECFE)", borderBottom: `2px solid ${INK}` }}>
            <tr style={{ color: INDIGO_DEEP }}>
              <th className="text-left px-3 py-2 font-semibold uppercase tracking-widest">Employee</th>
              <th className="text-left px-3 py-2 font-semibold uppercase tracking-widest">Role</th>
              <th className="text-right px-3 py-2 font-semibold uppercase tracking-widest">Days</th>
              <th className="text-right px-3 py-2 font-semibold uppercase tracking-widest">Gross</th>
              <th className="text-right px-3 py-2 font-semibold uppercase tracking-widest">Deductions</th>
              <th className="text-right px-3 py-2 font-semibold uppercase tracking-widest">To credit</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t" style={{ borderColor: "rgba(30,27,75,0.06)" }}>
                <td className="px-3 py-1.5 font-medium" style={{ color: INK }}>{r.emp}</td>
                <td className="px-3 py-1.5 opacity-70" style={{ color: INK }}>{r.role}</td>
                <td className="px-3 py-1.5 text-right font-mono opacity-70" style={{ color: INK }}>{r.days}</td>
                <td className="px-3 py-1.5 text-right font-mono" style={{ color: INK }}>{r.gross}</td>
                <td className="px-3 py-1.5 text-right font-mono" style={{ color: r.ded === "$0" ? "#94A3B8" : "#DC2626" }}>{r.ded}</td>
                <td className="px-3 py-1.5 text-right font-mono font-bold" style={{ color: "#059669" }}>{r.credit}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------------------
   3. Trust strip
----------------------------------------------------------------------------- */
function TrustStrip() {
  const brands = ["Northfield Digital", "The Ashwood Journal", "Silverlane Press", "Everbrite Studio", "Modern Canvas", "Lightspur", "Bluewick Media"];
  return (
    <section className="py-14 border-y relative z-10"
             style={{
               background: "linear-gradient(180deg, #F1E9D2 0%, #EBE0BF 100%)",
               borderColor: "rgba(30,27,75,0.08)",
             }}
             data-testid="trust-strip">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-center gap-3 mb-8">
          <span className="h-px w-8" style={{ background: INK, opacity: 0.15 }} />
          <p className="text-center text-[11px] font-semibold uppercase tracking-[0.24em]" style={{ color: INK, opacity: 0.6 }}>
            Trusted by content teams behind
          </p>
          <span className="h-px w-8" style={{ background: INK, opacity: 0.15 }} />
        </div>
        <div className="flex items-center justify-center gap-x-10 gap-y-4 flex-wrap">
          {brands.map((b) => (
            <span key={b}
                  data-testid={`trust-logo-${b.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-display font-bold tracking-tight text-lg md:text-xl opacity-50 hover:opacity-100 transition-opacity"
                  style={{ color: INK }}>
              {b}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -----------------------------------------------------------------------------
   4. Feature bento
----------------------------------------------------------------------------- */
const FEATURES = [
  {
    id: "one-home",       Icon: LayoutGrid,
    eyebrow: "One home for every story",
    title: "Everything, in one place.",
    desc: "Pitches, drafts, review notes, publish dates, performance — all sitting quietly next to each other, not scattered across five tools.",
    tone: "indigo", span: "md:col-span-2",
  },
  {
    id: "roles",          Icon: Users,
    eyebrow: "Editorial roles, out of the box",
    title: "The right people, on the right story.",
    desc: "Editors assign. Writers write. Publishers publish. Everyone sees exactly what belongs to them — nothing more, nothing less.",
    tone: "violet",
  },
  {
    id: "shape",          Icon: ClipboardCheck,
    eyebrow: "Your workflow, your columns",
    title: "Shape a project any way you like.",
    desc: "Drafts here, review links there, target keyword, publish date, whatever your team needs — add the columns you actually use.",
    tone: "indigo",
  },
  {
    id: "performance",    Icon: TrendingUp,
    eyebrow: "See what's working",
    title: "Watch stories earn their keep.",
    desc: "Track how each piece performs after it goes live and push the winners back into your promotion plan — without opening another tab.",
    tone: "emerald",
  },
  {
    id: "payroll",        Icon: Wallet,
    eyebrow: "Attendance in. Payslips out.",
    title: "The end of the month, without the panic.",
    desc: "Attendance rolls into payroll on its own. Everyone downloads their own payslip. Nobody chases spreadsheets on the last Friday.",
    tone: "amber",
  },
  {
    id: "many-teams",     Icon: Building2,
    eyebrow: "Grows with your masthead",
    title: "One workspace for every desk.",
    desc: "Editorial, SEO, marketing, HR — each with their own space, all sharing the same people, the same stories, the same truth.",
    tone: "indigo", span: "md:col-span-2",
  },
];
const TONE_BG = {
  indigo:  { pill: "bg-[#EEF0FE] text-[#4338CA]", icon: "from-indigo-400 to-indigo-600" },
  violet:  { pill: "bg-[#F3ECFE] text-[#7C3AED]", icon: "from-violet-400 to-violet-600" },
  emerald: { pill: "bg-[#E8F5EE] text-[#059669]", icon: "from-emerald-400 to-emerald-600" },
  amber:   { pill: "bg-[#FBF0DD] text-[#B45309]", icon: "from-amber-400 to-amber-600" },
};

function FeatureBento() {
  return (
    <section id="features" className="relative py-24 md:py-32"
             style={{
               background:
                 "linear-gradient(180deg, #F6EFDD 0%, #FBF6E9 30%, #FBF6E9 70%, #F4EAD3 100%)",
             }}
             data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mb-14 md:mb-16">
          <div className="text-xs font-bold uppercase tracking-[0.24em] mb-3" style={{ color: INDIGO }}>What's inside</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-[-0.02em] leading-[1.05]" style={{ color: INK }}>
            Six ways PLUTO makes<br />
            <span className="relative inline-block">
              <span style={{ color: INK }}>publishing feel light.</span>
              <Circle className="absolute -inset-2 w-[calc(100%+16px)] h-full" color={INDIGO} />
            </span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {FEATURES.map((f) => <FeatureCard key={f.id} f={f} />)}
        </div>
      </div>
    </section>
  );
}
function FeatureCard({ f }) {
  const Icon = f.Icon; const tone = TONE_BG[f.tone];
  return (
    <div data-testid={`feature-card-${f.id}`}
         className={`group relative rounded-3xl p-7 md:p-8 transition-all hover:-translate-y-1 hover:-rotate-[0.5deg] overflow-hidden ${f.span || ""}`}
         style={{
           background: "linear-gradient(160deg, #FEFCF4 0%, #FBF6E9 100%)",
           border: `2px dashed ${INK}`,
           boxShadow: "5px 5px 0 0 rgba(30,27,75,0.10)",
         }}>
      <div aria-hidden
           className={`absolute -top-16 -right-16 h-40 w-40 rounded-full opacity-0 group-hover:opacity-40 bg-gradient-to-br ${tone.icon} blur-3xl transition-opacity duration-500`} />
      <div className={`relative inline-flex h-12 w-12 rounded-2xl bg-gradient-to-br ${tone.icon} text-white items-center justify-center mb-5 shadow-lg`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className={`relative inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] mb-3 ${tone.pill}`}>
        {f.eyebrow}
      </div>
      <h3 className="relative font-display text-2xl md:text-[26px] font-bold tracking-[-0.01em] mb-2 leading-tight" style={{ color: INK }}>{f.title}</h3>
      <p className="relative text-[15px] leading-relaxed max-w-md" style={{ color: INK_SOFT }}>{f.desc}</p>
    </div>
  );
}

/* -----------------------------------------------------------------------------
   5. Product tour — alternating layout with product MOCKS pinned as polaroids
----------------------------------------------------------------------------- */
const TOUR = [
  {
    id: "tour-spreadsheet",
    eyebrow: "The sheet", caveat: "as fast as Google Sheets",
    title: "Move at the speed of a spreadsheet.",
    body: "Every story lives in a familiar grid. Filter by writer, sort by publish date, or bulk-update a whole month in seconds. Your editors will feel at home from minute one.",
    bullets: [
      "One tidy row per story — every field you actually use",
      "Filter, sort, update in bulk without lifting a mouse",
      "Handles thousands of pieces without slowing down",
    ],
    side: "right", Mock: SpreadsheetMock, browser: "pluto.app / sarah-miller",
  },
  {
    id: "tour-hierarchy",
    eyebrow: "The team", caveat: "everyone sees their own view",
    title: "The right people. The right story. Automatically.",
    body: "PLUTO knows your masthead. Editors see their desks. Writers see their assignments. Managers see the whole room. Nobody digs, nobody guesses.",
    bullets: [
      "Editorial roles ready on day one",
      "Share a story, a desk or a whole department in one click",
      "Managers get an aggregated view of everything below them",
    ],
    side: "left", Mock: HierarchyMock, browser: "pluto.app / team",
  },
  {
    id: "tour-payroll",
    eyebrow: "Payroll", caveat: "attendance in, payslips out",
    title: "End the payroll scramble.",
    body: "Attendance flows into payroll automatically. Bonuses, deductions and leave sit on the same page. Every teammate downloads a branded payslip themselves — you barely have to look.",
    bullets: [
      "Salary, attendance and leave in one live sheet",
      "Bank details flow from every profile — no re-typing",
      "One-tap payslip PDF for every teammate, every month",
    ],
    side: "right", Mock: PayrollMock, browser: "pluto.app / payroll",
  },
];
function ProductTour() {
  return (
    <section id="tour" className="py-24 md:py-32 relative"
             style={{
               background:
                 "linear-gradient(180deg, #F4EAD3 0%, #F1E9D2 40%, #EDDFB8 100%)",
             }}
             data-testid="tour-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mb-16 md:mb-20">
          <div className="text-xs font-bold uppercase tracking-[0.24em] mb-3" style={{ color: INDIGO }}>How it works</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-[-0.02em] leading-[1.05]" style={{ color: INK }}>
            A quick look at<br />
            <span className="font-hand" style={{ color: INDIGO_DEEP }}>the good bits.</span>
          </h2>
        </div>
        <div className="space-y-28 md:space-y-40">
          {TOUR.map((t, i) => <TourItem key={t.id} t={t} index={i} />)}
        </div>
      </div>
    </section>
  );
}
function TourItem({ t, index }) {
  const rotate = index === 1 ? 1.2 : -1.2;
  const Mock = t.Mock;
  const copy = (
    <div className="flex-1" data-testid={`${t.id}-copy`}>
      <div className="text-xs font-bold uppercase tracking-[0.24em] mb-2" style={{ color: INDIGO }}>{t.eyebrow}</div>
      <div className="font-hand text-2xl md:text-3xl mb-4 -mt-1" style={{ color: INDIGO_DEEP }}>— {t.caveat}</div>
      <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-[-0.02em] mb-5 leading-[1.05]" style={{ color: INK }}>
        {t.title}
      </h3>
      <p className="text-[16px] leading-relaxed mb-7 max-w-lg" style={{ color: INK_SOFT }}>{t.body}</p>
      <ul className="space-y-3">
        {t.bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-3 text-[15px]" style={{ color: INK }}>
            <CheckDoodle className="h-5 w-5 shrink-0 mt-0.5" color={INDIGO} />
            {b}
          </li>
        ))}
      </ul>
    </div>
  );
  const preview = (
    <div className="flex-1 min-w-0" data-testid={`${t.id}-preview`}>
      <PolaroidFrame rotate={rotate} testid={`${t.id}-mock`} browserLabel={t.browser}>
        <Mock />
      </PolaroidFrame>
    </div>
  );
  return (
    <div className={`flex flex-col ${t.side === "right" ? "md:flex-row" : "md:flex-row-reverse"} gap-10 md:gap-16 items-center`}>
      {copy}{preview}
    </div>
  );
}

/* -----------------------------------------------------------------------------
   6. Metrics band
----------------------------------------------------------------------------- */
function MetricsBand() {
  const metrics = [
    { v: "3 tools",  l: "replaced on day one" },
    { v: "1 sheet",  l: "instead of six spreadsheets" },
    { v: "1 hour",   l: "saved on payroll, every month" },
    { v: "0 chases", l: "for who published what" },
  ];
  return (
    <section className="py-20 md:py-24 relative overflow-hidden text-white"
             data-testid="metrics-band"
             style={{
               background:
                 "linear-gradient(135deg, #1E1B4B 0%, #312E81 35%, #5B21B6 70%, #7E22CE 100%)",
             }}>
      <div aria-hidden className="absolute inset-0 opacity-10">
        <svg viewBox="0 0 800 200" className="w-full h-full" preserveAspectRatio="none">
          <path d="M0 100 C 100 60, 200 140, 300 100 S 500 60, 600 100 S 800 140, 900 100" stroke="white" strokeWidth="2" fill="none" />
          <path d="M0 40 C 100 10, 200 70, 300 40 S 500 10, 600 40 S 800 70, 900 40" stroke="white" strokeWidth="1.5" fill="none" />
          <path d="M0 160 C 100 130, 200 190, 300 160 S 500 130, 600 160 S 800 190, 900 160" stroke="white" strokeWidth="1.5" fill="none" />
        </svg>
      </div>
      <div aria-hidden className="absolute -top-20 -right-20 h-72 w-72 rounded-full opacity-30 blur-[100px]" style={{ background: "linear-gradient(135deg, #F0ABFC 0%, #A855F7 100%)" }} />
      <div aria-hidden className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full opacity-30 blur-[100px]" style={{ background: "linear-gradient(135deg, #818CF8 0%, #4338CA 100%)" }} />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <div className="text-xs font-bold uppercase tracking-[0.24em] mb-3 text-indigo-200">The difference</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold tracking-[-0.02em] leading-[1.1]">Less admin. More stories.</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10 text-center">
          {metrics.map((m, i) => (
            <div key={i} data-testid={`metric-${i}`}>
              <div
                className="font-display text-4xl md:text-5xl font-bold tracking-tight"
                style={{
                  backgroundImage: "linear-gradient(135deg, #FFFFFF 0%, #DDD6FE 50%, #F0ABFC 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >{m.v}</div>
              <div className="mt-2 text-sm text-indigo-200 max-w-[220px] mx-auto">{m.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -----------------------------------------------------------------------------
   7. Comparison
----------------------------------------------------------------------------- */
const COMPARE_ROWS = [
  { label: "Feels like a spreadsheet",          pluto: true, notion: "partial", clickup: "partial", airtable: true      },
  { label: "Understands editorial roles",       pluto: true, notion: false,     clickup: false,     airtable: false     },
  { label: "Shape each project your way",       pluto: true, notion: true,      clickup: true,      airtable: true      },
  { label: "Attendance & leave included",       pluto: true, notion: false,     clickup: false,     airtable: false     },
  { label: "Payroll & payslips built in",       pluto: true, notion: false,     clickup: false,     airtable: false     },
  { label: "Track how each story performs",     pluto: true, notion: false,     clickup: false,     airtable: false     },
  { label: "Comfortable with a big backlog",    pluto: true, notion: false,     clickup: "partial", airtable: "partial" },
];
function ComparisonMatrix() {
  const cell = (v) => {
    if (v === true) return <CheckDoodle className="h-5 w-5 mx-auto" color={INDIGO} />;
    if (v === "partial") return <span className="inline-block h-2.5 w-2.5 rounded-full mx-auto" style={{ background: "#F59E0B" }} />;
    return <CrossDoodle className="h-5 w-5 mx-auto" color="#B4A99F" />;
  };
  return (
    <section id="compare" className="py-24 md:py-32"
             style={{ background: "linear-gradient(180deg, #EDDFB8 0%, #F1E9D2 15%, #FBF6E9 60%, #F6EFDD 100%)" }}
             data-testid="compare-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mb-14">
          <div className="text-xs font-bold uppercase tracking-[0.24em] mb-3" style={{ color: INDIGO }}>Why PLUTO</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-[-0.02em] leading-[1.05]" style={{ color: INK }}>
            Generic tools were<br />
            <span className="relative inline-block">
              <span>never built for us.</span>
              <SquiggleUnderline className="absolute left-0 -bottom-3 w-full h-4" color={INDIGO} />
            </span>
          </h2>
        </div>
        <div className="rounded-3xl overflow-hidden"
             style={{ background: "#FEFBF2", border: `2px solid ${INK}`, boxShadow: "6px 6px 0 0 rgba(30,27,75,0.14)" }}>
          <table className="w-full" data-testid="compare-table">
            <thead>
              <tr style={{ background: CREAM_ALT, borderBottom: `2px solid ${INK}` }}>
                <th className="text-left px-5 py-4 text-xs font-bold uppercase tracking-wider" style={{ color: INK_SOFT }}>Capability</th>
                <th className="px-5 py-4 text-center">
                  <div className="inline-flex items-center gap-1.5">
                    <img src="/logo/pluto-logo.png" alt="PLUTO" className="h-5 w-5 object-contain" />
                    <span className="font-brand tracking-brand text-sm" style={{ color: INK }}>PLUTO</span>
                  </div>
                </th>
                <th className="px-5 py-4 text-center text-xs font-semibold" style={{ color: INK_SOFT }}>Notion</th>
                <th className="px-5 py-4 text-center text-xs font-semibold" style={{ color: INK_SOFT }}>ClickUp</th>
                <th className="px-5 py-4 text-center text-xs font-semibold" style={{ color: INK_SOFT }}>Airtable</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((r, i) => (
                <tr key={i} data-testid={`compare-row-${i}`} style={{ borderTop: "1px dashed rgba(30,27,75,0.15)" }}>
                  <td className="px-5 py-3.5 text-sm font-medium" style={{ color: INK }}>{r.label}</td>
                  <td className="px-5 py-3.5 text-center" style={{ background: "rgba(199,210,254,0.25)" }}>{cell(r.pluto)}</td>
                  <td className="px-5 py-3.5">{cell(r.notion)}</td>
                  <td className="px-5 py-3.5">{cell(r.clickup)}</td>
                  <td className="px-5 py-3.5">{cell(r.airtable)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center gap-6 justify-center mt-5 text-[11px]" style={{ color: INK_SOFT }}>
          <span className="inline-flex items-center gap-1.5"><CheckDoodle className="h-4 w-4" color={INDIGO} /> Built-in</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: "#F59E0B" }} /> Needs setup</span>
          <span className="inline-flex items-center gap-1.5"><CrossDoodle className="h-4 w-4" color="#B4A99F" /> Not there</span>
        </div>
      </div>
    </section>
  );
}

/* -----------------------------------------------------------------------------
   8. Testimonials — post-it notes
----------------------------------------------------------------------------- */
const QUOTES = [
  { q: "We killed three tools the week we moved. Editorial finally stopped hunting for links in Slack.",
    a: "Sarah Miller",   r: "Managing Editor · Northfield Digital",  tint: "#FDE68A", rotate: -1.5 },
  { q: "Our writers used to hate our project tracker. Now they open it first thing in the morning.",
    a: "James Anderson", r: "Head of Content · Silverlane Press",    tint: "#DDD6FE", rotate:  1.3 },
  { q: "Payroll went from a two-day rebuild to a click. Our team leads actually trust the numbers now.",
    a: "Emma Thompson",  r: "Ops Manager · The Ashwood Journal",     tint: "#BBF7D0", rotate: -0.7 },
];
function Testimonials() {
  return (
    <section className="py-24 md:py-32"
             style={{ background: "linear-gradient(180deg, #F6EFDD 0%, #F1E9D2 50%, #EEE4C4 100%)" }}
             data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mb-14 md:mb-16">
          <div className="text-xs font-bold uppercase tracking-[0.24em] mb-3" style={{ color: INDIGO }}>Kind words</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-[-0.02em] leading-[1.05]" style={{ color: INK }}>
            Editorial teams who<br />
            <span className="font-hand" style={{ color: INDIGO_DEEP }}>ship more with PLUTO.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
          {QUOTES.map((q, i) => (
            <figure key={i} data-testid={`testimonial-${i}`}
                    className="relative rounded-2xl p-7 transition-all hover:-translate-y-1"
                    style={{ background: q.tint, transform: `rotate(${q.rotate}deg)`,
                             border: `2px solid ${INK}`, boxShadow: "6px 6px 0 0 rgba(30,27,75,0.14)" }}>
              <span aria-hidden className="absolute -top-2 left-8 h-5 w-16 rounded-sm rotate-[-6deg] opacity-90"
                    style={{ background: "repeating-linear-gradient(45deg, rgba(255,255,255,0.85) 0 6px, rgba(255,255,255,0.6) 6px 12px)" }} />
              <div className="flex items-center gap-0.5 mb-3">
                {[0,1,2,3,4].map((s) => <Star key={s} className="h-3.5 w-3.5 fill-current" style={{ color: INDIGO_DEEP }} />)}
              </div>
              <blockquote className="text-[16px] leading-relaxed" style={{ color: INK }}>"{q.q}"</blockquote>
              <figcaption className="mt-6">
                <div className="text-sm font-semibold" style={{ color: INK }}>{q.a}</div>
                <div className="text-xs mt-0.5" style={{ color: INK_SOFT }}>{q.r}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -----------------------------------------------------------------------------
   9. Final CTA
----------------------------------------------------------------------------- */
function FinalCta({ onCta }) {
  return (
    <section id="pricing" data-testid="final-cta-section" className="relative py-24 md:py-32 overflow-hidden"
             style={{
               background:
                 "radial-gradient(80% 100% at 50% 100%, rgba(196,181,253,0.55) 0%, rgba(221,214,254,0.35) 40%, #FBF6E9 100%)",
             }}>
      <div aria-hidden className="absolute inset-0 flex items-center justify-center opacity-[0.06]">
        <svg viewBox="0 0 400 400" className="w-[600px] h-[600px]">
          <g stroke={INDIGO} strokeWidth="2" strokeLinecap="round">
            {Array.from({ length: 24 }).map((_, i) => {
              const angle = (i * 360) / 24; const rad = (angle * Math.PI) / 180;
              const x1 = 200 + Math.cos(rad) * 60; const y1 = 200 + Math.sin(rad) * 60;
              const x2 = 200 + Math.cos(rad) * 180; const y2 = 200 + Math.sin(rad) * 180;
              return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
            })}
          </g>
        </svg>
      </div>
      <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
        <div className="inline-flex items-center gap-2 mb-4">
          <Sparkles className="h-4 w-4" style={{ color: INDIGO }} />
          <span className="text-xs font-bold uppercase tracking-[0.24em]" style={{ color: INDIGO }}>Ready when you are</span>
          <Sparkles className="h-4 w-4" style={{ color: INDIGO }} />
        </div>
        <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold tracking-[-0.02em] leading-[1.02]" style={{ color: INK }}>
          Bring your{" "}
          <span className="relative inline-block whitespace-nowrap">
            <span style={{
              backgroundImage: "linear-gradient(135deg, #4338CA 0%, #7C3AED 50%, #C026D3 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}>whole newsroom</span>
            <SquiggleUnderline className="absolute left-0 -bottom-3 w-full h-4" color={INDIGO} />
          </span>
          <br />into one warm place.
        </h2>
        <p className="mt-8 text-base md:text-lg max-w-xl mx-auto" style={{ color: INK_SOFT }}>
          Free 14-day trial. No credit card. Invite your whole team. Cancel any time — we'll help you export everything if it isn't the right fit.
        </p>
        <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button onClick={onCta} data-testid="final-cta-primary"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-sm font-semibold text-white transition-all hover:-translate-y-0.5 shadow-[0_6px_0_0_#1E1B4B] hover:shadow-[0_8px_0_0_#1E1B4B]"
                  style={{ background: "linear-gradient(135deg, #4338CA 0%, #6D28D9 55%, #A21CAF 100%)" }}>
            Start free trial <ArrowRight className="h-4 w-4" />
          </button>
          <Link to="/dash" data-testid="final-cta-secondary"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-sm font-semibold transition-all hover:-translate-y-0.5"
                style={{ color: INK, border: `2px solid ${INK}` }}>
            Peek at the dashboard <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* -----------------------------------------------------------------------------
   10. Footer
----------------------------------------------------------------------------- */
function LandingFooter() {
  const groups = [
    { title: "Product",  links: [
      { label: "Features",     href: "#features" }, { label: "How it works", href: "#tour" },
      { label: "Compare",      href: "#compare"  }, { label: "Pricing",      href: "#pricing" },
    ]},
    { title: "Company",  links: [
      { label: "About CreateX", href: "#" }, { label: "Careers", href: "#" },
      { label: "Blog", href: "#" },          { label: "Contact", href: "#" },
    ]},
    { title: "Help",     links: [
      { label: "Documentation", href: "#" }, { label: "What's new", href: "#" },
      { label: "Status", href: "#" },        { label: "Talk to us", href: "#" },
    ]},
    { title: "Legal",    links: [
      { label: "Privacy", href: "#" }, { label: "Terms", href: "#" },
      { label: "Security", href: "#" }, { label: "DPA", href: "#" },
    ]},
  ];
  return (
    <footer style={{ background: "linear-gradient(180deg, #312E81 0%, #1E1B4B 60%, #0F0D30 100%)", color: "#C7D2FE" }} data-testid="landing-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-10">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-10 md:gap-6">
          <div className="col-span-2">
            <Link to="/" className="flex items-center gap-2.5">
              <img src="/logo/pluto-logo.png" alt="PLUTO" className="h-9 w-9 object-contain" />
              <div className="flex flex-col leading-none">
                <span className="font-brand tracking-brand text-lg text-white">PLUTO</span>
                <span className="text-[9px] font-medium tracking-[0.18em] uppercase text-indigo-300 mt-0.5">by CreateX</span>
              </div>
            </Link>
            <p className="mt-4 text-sm leading-relaxed max-w-xs opacity-80">
              A home for every story your team tells — from the first idea to the end-of-month payslip.
            </p>
            <p className="mt-3 font-hand text-lg" style={{ color: "#DDD6FE" }}>— made with care in Bengaluru.</p>
          </div>
          {groups.map((g) => (
            <div key={g.title} data-testid={`footer-group-${g.title.toLowerCase()}`}>
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-white mb-4">{g.title}</div>
              <ul className="space-y-2.5">
                {g.links.map((l) => (
                  <li key={l.label}>
                    <a href={l.href} className="text-sm opacity-70 hover:opacity-100 hover:text-white transition-opacity">{l.label}</a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-16 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-xs opacity-70">
          <div>© {new Date().getFullYear()} CreateX Agency. All rights reserved.</div>
          <div className="flex items-center gap-1.5"><Zap className="h-3.5 w-3.5" /> Built to help teams ship more, stress less.</div>
        </div>
      </div>
    </footer>
  );
}
